import {
  CheckBox,
  DateBox,
  Popup,
  ScrollView,
  TextBox,
} from "devextreme-react";
import Button from "devextreme-react/button";
import { ICheckBoxOptions } from "devextreme-react/check-box";
import {
  Column,
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  Toolbar,
} from "devextreme-react/data-grid";
import {
  ButtonItem,
  Form,
  FormRef,
  GroupItem,
  Item,
} from "devextreme-react/form";
import { LookupTypes } from "devextreme-react/lookup";
import notify from "../../lib/notify";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAward,
  deleteAward,
  fetchAwards,
  updateAward,
} from "../../api/awards";
import {
  fetchSubordinateEmployeesWithLoaned,
  fetchSubordinateManagersForManager,
} from "../../api/calendarData";
import {
  getProjectsAvailableForUser,
  getProjectsForEmployee,
} from "../../api/projects";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./Awards.scss";

interface AwardRow {
  id: number;
  employeeId: number;
  companyId: number;
  employeeName: string;
  employeeSurname: string;
  from: Date;
  to: Date;
  sum: number;
  managerId: number;
  managerCompanyId: number;
  managerName: number;
  managerSurname: number;
  approvingEmployeeName: string;
  approvingEmployeeSurname: string;
  projectName: number;
  awardTyp: string;
  dateCreated: Date;
  approvalDate: Date;
  confirmed: number;
  note: string;
}

const formatDate = (d: Date | string) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${date.getFullYear()}`;
};

export const Awards = () => {
  const [projects, setProjects] = useState<
    {
      id: number;
      acronym: string;
    }[]
  >([]);
  const [users, setUsers] = useState<
    {
      id: number;
      companyId: number;
      code: number;
      firstName: string;
      lastName: string;
      isBorrowed: boolean;
      borrowedByManagerId: number;
    }[]
  >([]);
  const { user } = useAuth();
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState<number>();
  const [employeeCompanyId, setEmployeeCompanyId] = useState<number>();
  const [popupVisibleAdd, setPopupVisibleAdd] = useState(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [price, setPrice] = useState<number>(1);
  const [displayValue, setDisplayValue] = useState<number>(0);
  const [precent, setPrecent] = useState<boolean>(false);
  const [hourWorked, setHourWorked] = useState<boolean>(false);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [employeeProjectIds, setEmployeeProjectIds] = useState<number[]>([]);
  const [notes, setNotes] = useState<string>("");
  const { companyId } = useCompanyContext();
  const formRef = useRef<FormRef | null>(null);
  const validateAll = () => {
    return formRef.current?.instance().validate();
  };
  const [awards, setAwards] = useState<AwardRow[]>([]);
  const currentDate = new Date();
  const today = new Date();
  const firstDayOfCurrentMonth = new Date(
    today.getFullYear(),
    today.getMonth(),
    1,
  );

  const month = String(firstDayOfCurrentMonth.getMonth() + 1).padStart(2, "0");
  const year = firstDayOfCurrentMonth.getFullYear();
  const formatted = `${year}-${month}`;
  const companyOptions = user?.companyId;
  const [selectedMonth, setSelectedMonth] = useState<string>(formatted);

  const [startDate, setStartDate] = useState(() => {
    const twoWeeksBack = new Date(currentDate);
    twoWeeksBack.setDate(currentDate.getDate() - 14);
    return twoWeeksBack;
  });

  const [endDate, setEndDate] = useState(() => {
    const twoWeeksForward = new Date(currentDate);
    twoWeeksForward.setDate(currentDate.getDate() + 14);
    return twoWeeksForward;
  });
  const role = user?.roles;
  const isStaff = role && role.includes(RoleEnum.KadrySpolki);
  const admin = role && role.includes(RoleEnum.AdministratorSpolki);
  const headAdmin = role && role.includes(RoleEnum.AdministratorGlowny);
  const isManager = role && role.includes(RoleEnum.Kierownik);
  const isManagerManager = role && role.includes(RoleEnum.Manager);
  const [showApproved, setShowApproved] = useState(false);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [mobileSearch, setMobileSearch] = useState("");

  const filteredAwards = useMemo(() => {
    const q = mobileSearch.trim().toLowerCase();
    if (!q) return awards;
    return awards.filter((a) =>
      [
        a.employeeSurname,
        a.employeeName,
        a.managerSurname,
        a.managerName,
        a.projectName,
        a.awardTyp,
        a.note,
      ]
        .filter(Boolean)
        .some((v) => v!.toString().toLowerCase().includes(q)),
    );
  }, [awards, mobileSearch]);

  useEffect(() => {
    fetchAwardsTo();
  }, [startDate, endDate, showApproved]);

  useEffect(() => {
    setDisplayValue(precent ? price / 100 : price);
  }, [price, precent]);

  useEffect(() => {
    fetchDataForSubordinateEmployess();
    fetchAwardsTo();
  }, [companyId]);

  const fetchDataForSubordinateEmployess = async () => {
    try {
      if (isManagerManager) {
        const response = await fetchSubordinateManagersForManager();
        if (response.isOk) {
          setUsers(response.data);
        } else {
          setUsers([]);
        }
        return;
      }

      let company = 0;

      if (companyId === 0) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }
      const toIsoDate = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate(),
        ).padStart(2, "0")}`;
      const response = await fetchSubordinateEmployeesWithLoaned(
        company,
        user?.employeeId || 0,
        toIsoDate(startDate),
        toIsoDate(endDate),
      );
      if (response.isOk) {
        setUsers(response.data);
      } else {
        const usersList = response.data.data || response.data || [];
        setUsers(usersList);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  // const fetchDataProjectsEmployee = async (
  //   employeeId: number,
  //   companyId: number,
  // ) => {
  //   const responseName = await downloadEmployeeProjects(employeeId, companyId);

  //   if (responseName.data && Array.isArray(responseName.data)) {
  //     setProjects(responseName.data);
  //   } else {
  //     const namesList = responseName.data.data || [];
  //     setProjects(namesList);
  //   }
  // };

  const fetchAwardsTo = async () => {
    try {
      let company = 0;

      if (companyId === 0) {
        company = user?.companyId || 0;
      } else {
        company = companyId;
      }

      const responseName = await fetchAwards(
        company,
        startDate,
        endDate,
        showApproved,
      );

      if (responseName.data && Array.isArray(responseName.data)) {
        setAwards(responseName.data);
      } else {
        const namesList = responseName.data.data || [];
        setAwards(namesList);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const addAward = async () => {
    if (!employeeId || !employeeCompanyId) {
      notify(t("Wybierz pracownika"), "warning");
      return;
    }
    try {
      let typeAward = "";
      if (precent === true) {
        typeAward = "prec.";
      } else if (hourWorked === true) {
        typeAward = "godz.";
      } else {
        typeAward = "wal.";
      }

      let confirm = 0;
      if (confirmed === true) {
        confirm = 1;
      } else {
        confirm = 0;
      }

      const result = await createAward(
        [employeeId],
        [employeeCompanyId],
        selectedMonth,
        price,
        typeAward,
        projectId || 0,
        confirm,
        notes,
      );
      if (result.isOk) {
        notify(t("Award send."), "success");
        await fetchAwardsTo();
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }

    setEmployeeId(undefined);
    setEmployeeCompanyId(undefined);
    setSelectedMonth(formatted);
    setPrice(1);
    setProjectId(null);
    setConfirmed(false);
    setNotes("");
    setHourWorked(false);
  };

  const acceptAward = async (projectId: number) => {
    try {
      const result = await updateAward(projectId, 1);
      if (result.isOk) {
        notify(t("Award accepted."), "success");
        await fetchAwardsTo();
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }
  };

  const removeAward = async (projectId: number) => {
    try {
      const result = await deleteAward(projectId);
      if (result.isOk) {
        notify(t("Award deleted."), "success");
        await fetchAwardsTo();
      } else {
        notify(`${t("Error:")} ${result.error || t("Unknown error")}`, "error");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notify(
          `${t("Error:")} ${error.message || t("Unknown error")}`,
          "error",
        );
      }
    }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t("awards.awards"));
      worksheet.columns = [
        { header: t("awards.employee"), key: "employee", width: 30 },
        { header: t("awards.value"), key: "award", width: 14 },
        { header: t("awards.from"), key: "from", width: 14 },
        { header: t("awards.to"), key: "to", width: 14 },
        { header: t("awards.project"), key: "project", width: 20 },
        { header: t("awards.note"), key: "note", width: 30 },
        { header: t("awards.manager"), key: "manager", width: 30 },
      ];
      awards.forEach((a) => {
        worksheet.addRow({
          employee: `${a.employeeSurname || ""} ${a.employeeName || ""}`.trim(),
          award: a.sum,
          from: formatDate(a.from),
          to: formatDate(a.to),
          project: a.projectName,
          note: a.note,
          manager: `${a.managerSurname || ""} ${a.managerName || ""}`.trim(),
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `Nagrody_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const acceptPopup = (award: any) => {
    acceptAward(award.id);
  };

  const deletePopup = (award: any) => {
    removeAward(award.id);
  };

  const addPopup = (e) => {
    fetchDataForSubordinateEmployess();
    setPopupVisibleAdd(true);
  };

  const fetchProjectsAvailableForUser = useCallback(async () => {
    if (isManagerManager) return;
    try {
      setProjects(
        await getProjectsAvailableForUser(
          !headAdmin || companyId === 0 ? undefined : companyId,
        ),
      );
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    }
  }, [companyId, t, isManagerManager]);

  useEffect(() => {
    fetchProjectsAvailableForUser();
  }, [fetchProjectsAvailableForUser]);

  useEffect(() => {
    if (!employeeId || !employeeCompanyId) {
      setEmployeeProjectIds([]);
      if (isManagerManager) setProjects([]);
      return;
    }
    if (isManagerManager) {
      getProjectsForEmployee(employeeId, employeeCompanyId)
        .then((empProjects) => {
          setProjects(empProjects);
          setEmployeeProjectIds(empProjects.map((p) => p.id));
        })
        .catch(() => {
          setProjects([]);
          setEmployeeProjectIds([]);
        });
    } else {
      getProjectsForEmployee(employeeId, employeeCompanyId)
        .then((empProjects) =>
          setEmployeeProjectIds(empProjects.map((p) => p.id)),
        )
        .catch(() => setEmployeeProjectIds([]));
    }
  }, [employeeId, employeeCompanyId, isManagerManager]);

  const canAccept = !!(isStaff || admin || headAdmin);
  const canDelete = !!(isStaff || admin || headAdmin || isManager);

  const isDeleteDisabled = (award: any) => {
    if (award.confirmed === 1) return true;
    if (isManager) {
      return award.confirmed === 1 || award.confirmed === -1;
    }
    return false;
  };

  return (
    <ScrollView useNative>
      <div className={isMobile ? "p-4" : "p-6"}>
        <div className="mb-2 text-2xl font-bold leading-none">
          {t("awards.awards")}
        </div>
        <div className="mb-1 text-base">{t("awards.headerText")}</div>
        <div className="mb-3 flex items-center gap-2">
          <CheckBox
            value={showApproved}
            onValueChanged={(e) => setShowApproved(e.value)}
          />
          <span>{t("Pokaż zatwierdzone")}</span>
        </div>
        {isMobile ? (
          <>
            <div className="sticky top-0 z-10 flex flex-col gap-2 py-2 bg-white dark:bg-[#1e1e1e]">
              <Button
                type="default"
                icon="plus"
                text={t("awards.addAward")}
                width="100%"
                onClick={addPopup}
              />
              <div className="grid grid-cols-2 gap-2">
                <DateBox
                  value={startDate}
                  onValueChanged={(e: any) => setStartDate(e.value)}
                  placeholder={t("awards.startDate")}
                  label={t("awards.startDate")}
                  labelMode="static"
                  width="100%"
                />
                <DateBox
                  value={endDate}
                  onValueChanged={(e: any) => setEndDate(e.value)}
                  placeholder={t("awards.endDate")}
                  label={t("awards.endDate")}
                  labelMode="static"
                  width="100%"
                />
              </div>
              <TextBox
                value={mobileSearch}
                onValueChanged={(e) => setMobileSearch(e.value || "")}
                placeholder={t("searchPlaceholder")}
                showClearButton
                width="100%"
                mode="search"
              />
            </div>
            <div className="flex flex-col gap-3 pb-4 mt-2">
              {filteredAwards.length === 0 ? (
                <div
                  className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
                  role="alert"
                >
                  {t("noSubordinateEmployees")}
                </div>
              ) : (
                filteredAwards.map((award) => {
                  const isConfirmed = award.confirmed === 1;
                  const isRejected = award.confirmed === -1;
                  return (
                    <div
                      key={award.id}
                      className={`rounded-xl border shadow-sm overflow-hidden ${
                        isConfirmed
                          ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-700"
                          : "border-black/10 dark:border-white/10 bg-white dark:bg-[#1e1e1e]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2 border-b border-black/5 dark:border-white/5">
                        <div className="min-w-0">
                          <div className="text-base font-bold break-words">
                            {`${award.employeeSurname || ""} ${
                              award.employeeName || ""
                            }`.trim()}
                          </div>
                          {award.projectName && (
                            <div className="text-xs opacity-70 mt-0.5 break-words">
                              {award.projectName}
                            </div>
                          )}
                        </div>
                        {isConfirmed && (
                          <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-600 text-white text-[11px] font-semibold px-2 py-1">
                            {t("awards.confirm")}
                          </span>
                        )}
                        {isRejected && (
                          <span className="shrink-0 inline-flex items-center rounded-full bg-rose-600 text-white text-[11px] font-semibold px-2 py-1">
                            {t("awards.remove")}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 p-4 text-sm">
                        <div className="flex flex-col">
                          <span className="text-[11px] uppercase opacity-70">
                            {t("awards.from")}
                          </span>
                          <span className="font-semibold tabular-nums">
                            {formatDate(award.from)}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] uppercase opacity-70">
                            {t("awards.to")}
                          </span>
                          <span className="font-semibold tabular-nums">
                            {formatDate(award.to)}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] uppercase opacity-70">
                            {t("awards.value")}
                          </span>
                          <span className="font-semibold tabular-nums">
                            {award.sum}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] uppercase opacity-70">
                            {t("awards.manager")}
                          </span>
                          <span className="font-semibold break-words">
                            {`${award.managerSurname || ""} ${
                              award.managerName || ""
                            }`.trim() || "—"}
                          </span>
                        </div>
                        {award.note && (
                          <div className="flex flex-col">
                            <span className="text-[11px] uppercase opacity-70">
                              {t("awards.note")}
                            </span>
                            <span className="break-words">{award.note}</span>
                          </div>
                        )}
                        {(isStaff || admin || headAdmin) &&
                          (award.approvingEmployeeSurname ||
                            award.approvingEmployeeName) && (
                            <div className="flex flex-col">
                              <span className="text-[11px] uppercase opacity-70">
                                {t("awards.approvingEmployee")}
                              </span>
                              <span className="font-semibold break-words">
                                {`${award.approvingEmployeeSurname || ""} ${
                                  award.approvingEmployeeName || ""
                                }`.trim()}
                              </span>
                            </div>
                          )}
                      </div>
                      {(canAccept || canDelete) && (
                        <div className="flex flex-col gap-2 px-4 pb-4 pt-1">
                          {canAccept && award.confirmed === 0 && (
                            <Button
                              icon="check"
                              type="success"
                              stylingMode="contained"
                              text={t("awards.acceptAward")}
                              width="100%"
                              onClick={() => acceptPopup(award)}
                            />
                          )}
                          {canDelete && !isDeleteDisabled(award) && (
                            <Button
                              icon="trash"
                              type="danger"
                              stylingMode="outlined"
                              text={t("awards.remove")}
                              width="100%"
                              onClick={() => deletePopup(award)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <DataGrid
            dataSource={awards}
            keyExpr="id"
            showBorders
            allowColumnResizing
            allowColumnReordering
            searchPanel={{
              visible: true,
              placeholder: t("searchPlaceholder"),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
            onRowPrepared={(e) => {
              if (e.rowType === "data" && e.data?.confirmed === 1) {
                e.rowElement.style.backgroundColor = "#d1fae5";
              }
            }}
          >
            <Toolbar>
              <DataGridItem
                name="searchPanel"
                location="before"
                locateInMenu="auto"
                cssClass="datagrid-search-panel-without-margin-left"
              />
              <DataGridItem
                location="after"
                locateInMenu="auto"
                widget="dxButton"
                options={{
                  icon: "exportxlsx",
                  hint: t("Export all"),
                  stylingMode: "contained",
                  onClick: exportToExcel,
                  elementAttr: {
                    class:
                      "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-medium px-4 py-2 rounded-lg shadow-md transition duration-150 ease-in-out mr-2",
                  },
                }}
              />
              <DataGridItem location="after" locateInMenu="auto">
                <Button
                  type="default"
                  text={t("awards.addAward")}
                  onClick={addPopup}
                />
              </DataGridItem>
              <DataGridItem location="center" locateInMenu="auto">
                <div className="flex gap-2">
                  <DateBox
                    value={startDate}
                    onValueChanged={(e: any) => setStartDate(e.value)}
                    placeholder={t("awards.startDate")}
                    label={t("awards.startDate")}
                    labelMode="static"
                    width={150}
                  />
                  <DateBox
                    value={endDate}
                    onValueChanged={(e: any) => setEndDate(e.value)}
                    placeholder={t("awards.endDate")}
                    label={t("awards.endDate")}
                    labelMode="static"
                    width={150}
                  />
                </div>
              </DataGridItem>
            </Toolbar>
            <FilterRow visible />
            <Column
              calculateCellValue={(row: AwardRow) =>
                `${row.employeeSurname} ${row.employeeName}`
              }
              name="employeeFullName"
              dataType="string"
              caption={t("awards.employee")}
              allowFiltering
              allowSearch
              allowSorting
              minWidth={200}
              fixed
              fixedPosition="left"
            />
            <Column
              dataField="sum"
              dataType="string"
              caption={t("awards.value")}
              minWidth={150}
            />
            <Column
              dataField="from"
              dataType="datetime"
              format="dd.MM.yyyy"
              caption={t("awards.from")}
              minWidth={125}
            />
            <Column
              dataField="to"
              dataType="datetime"
              format="dd.MM.yyyy"
              caption={t("awards.to")}
              minWidth={125}
            />
            <Column
              dataField="projectName"
              dataType="string"
              caption={t("awards.project")}
              minWidth={150}
            />
            <Column
              dataField="note"
              dataType="string"
              caption={t("awards.note")}
              minWidth={150}
            />
            <Column
              calculateCellValue={(row: AwardRow) =>
                `${row.managerSurname} ${row.managerName}`
              }
              name="managerFullName"
              dataType="string"
              caption={t("awards.manager")}
              allowFiltering
              allowSearch
              allowSorting
              minWidth={200}
            />
            <Column
              calculateCellValue={(row: AwardRow) =>
                `${row.approvingEmployeeSurname} ${row.approvingEmployeeName}`
              }
              name="approvingEmployeeFullName"
              dataType="string"
              caption={t("awards.approvingEmployee")}
              allowFiltering
              allowSearch
              allowSorting
              minWidth={200}
            />
            {(canAccept || canDelete) && (
              <Column
                type="buttons"
                caption={t("awards.actions")}
                name="actions"
                buttons={[
                  {
                    icon: "check",
                    onClick: (e) => {
                      const rowData = e.row?.data;
                      if (rowData && !rowData.isRegistered) {
                        acceptPopup(rowData);
                      }
                    },
                    disabled: (e) => {
                      const rowData = e.row?.data;
                      return rowData?.confirmed === 1;
                    },
                    hint: t("awards.acceptAward"),
                    visible: (e) => {
                      const rowData = e.row?.data;
                      return !!rowData && canAccept && rowData.confirmed === 0;
                    },
                  },
                  {
                    icon: "trash",
                    onClick: (e) => {
                      const rowData = e.row?.data;
                      if (rowData && !rowData.isRegistered) {
                        deletePopup(rowData);
                      }
                    },
                    disabled: (e) => {
                      const rowData = e.row?.data;
                      if (rowData?.confirmed === 1) return true;
                      if (isManager) {
                        return (
                          rowData?.confirmed === 1 || rowData?.confirmed === -1
                        );
                      }
                      return rowData ? rowData.isRegistered : false;
                    },
                    hint: t("awards.remove"),
                    visible: (e) => {
                      const rowData = e.row?.data;
                      if (!rowData) return false;
                      if (!canDelete) return false;
                      if (rowData.confirmed === 1) return false;
                      if (isManager) {
                        return rowData.confirmed !== 1;
                      }
                      return true;
                    },
                  },
                ]}
              />
            )}
          </DataGrid>
        )}

        {/* Popup do dodwania nagrody */}
        <Popup
          visible={popupVisibleAdd}
          onHiding={() => setPopupVisibleAdd(false)}
          fullScreen={isMobile}
          maxWidth={isMobile ? undefined : 500}
          height={isMobile ? "100%" : "auto"}
          maxHeight={isMobile ? undefined : "80vh"}
          title={t("awards.addAward")}
          showCloseButton
          dragEnabled={!isMobile}
        >
          <Form ref={formRef} validationGroup="schemaGroup">
            <GroupItem>
              <Item
                dataField="employee"
                label={{
                  text: t("availabilityPopup.employee"),
                }}
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: users.filter((u) => user),
                  searchEnabled: true,
                  searchTimeout: 0,
                  showDataBeforeSearch: true,
                  valueExpr: "id",
                  displayExpr: (user) =>
                    user ? `${user.lastName} ${user.firstName}` : "",
                  value: employeeId,
                  onValueChanged: (event: any) => {
                    const employee = users.find((u) => u.id === event.value);
                    setEmployeeId(employee?.id);
                    setEmployeeCompanyId(employee?.companyId);
                  },
                }}
                validationRules={[
                  {
                    type: "required",
                    message: t("sheets.schemaNameRequired"),
                  },
                ]}
              />
              <Item
                dataField={t("awards.project")}
                editorType="dxLookup"
                cssClass="lookup-dx"
                editorOptions={
                  {
                    dataSource: projects,
                    displayExpr: "acronym",
                    valueExpr: "id",
                    value: projectId,
                    onValueChanged: (e: any) => setProjectId(e.value),
                    searchEnabled: true,
                    dropDownOptions: {
                      showTitle: false,
                    },
                  } as LookupTypes.Properties
                }
              />
              <Item
                visible={
                  !!employeeId &&
                  !!projectId &&
                  !employeeProjectIds.includes(projectId)
                }
              >
                <span
                  style={{
                    display: "inline-block",
                    backgroundColor: "#fff3cd",
                    color: "#856404",
                    border: "1px solid #ffc107",
                    borderRadius: "12px",
                    padding: "2px 10px",
                    fontSize: "12px",
                  }}
                >
                  {t(
                    "awards.employeeNotAssignedToProject",
                    "Pracownik nie jest przypisany do tego projektu",
                  )}
                </span>
              </Item>
            </GroupItem>
            <GroupItem colCount={2} cssClass="group-item-with-time">
              <Item
                dataField={t("awards.startDate")}
                editorType="dxDateBox"
                editorOptions={{
                  value: selectedMonth ? new Date(selectedMonth + "-01") : null,
                  width: 200,
                  type: "date",
                  displayFormat: "monthAndYear",
                  onValueChanged: (e: any) => {
                    const date = new Date(e.value);
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = date.getFullYear();
                    const formatted = `${year}-${month}`;
                    setSelectedMonth(formatted);
                  },
                  openOnFieldClick: true,
                  useMaskBehavior: true,
                  showClearButton: false,
                  calendarOptions: {
                    maxZoomLevel: "year",
                    minZoomLevel: "century",
                  },
                }}
                validationRules={[
                  {
                    type: "required",
                  },
                ]}
              />
            </GroupItem>
            {isStaff && (
              <Item
                dataField={t("awards.confirm")}
                editorType="dxCheckBox"
                label={{
                  visible: false,
                }}
                editorOptions={
                  {
                    value: confirmed,
                    onValueChanged: (e: any) => setConfirmed(e.value),
                    text: t("awards.confirm"),
                  } as ICheckBoxOptions
                }
              />
            )}
            {companyOptions === 1 && (
              <GroupItem>
                <Item
                  dataField={t("awards.precentValue")}
                  editorType="dxCheckBox"
                  label={{
                    visible: false,
                  }}
                  editorOptions={
                    {
                      value: precent,
                      onValueChanged: (e: any) => {
                        setHourWorked(false);
                        setPrecent(e.value);
                      },
                      text: t("awards.precentValue"),
                    } as ICheckBoxOptions
                  }
                />
                <Item
                  dataField={t("awards.hourWorked")}
                  editorType="dxCheckBox"
                  label={{
                    visible: false,
                  }}
                  editorOptions={
                    {
                      value: hourWorked,
                      onValueChanged: (e: any) => {
                        setHourWorked(e.value);
                        setPrecent(false);
                      },
                      text: t("awards.hourWorked"),
                    } as ICheckBoxOptions
                  }
                />
              </GroupItem>
            )}
            <Item
              dataField={t("awards.value")}
              editorType="dxNumberBox"
              cssClass="numberbox-dx"
              editorOptions={{
                value: displayValue,
                width: 200,
                format: precent ? "#0%" : "#,##0.00",
                onValueChanged: (e: any) => {
                  const newValue = precent ? e.value * 100 : e.value;
                  if (newValue !== price) {
                    setPrice(newValue);
                  }
                },
                showSpinButtons: true,
                min: 1,
              }}
              validationRules={[
                {
                  type: "required",
                },
              ]}
            />
            <Item
              dataField={t("awards.note")}
              editorType="dxTextArea"
              editorOptions={{
                value: notes,
                width: "100%",
                height: 100,
                onValueChanged: (e: any) => setNotes(e.value),
              }}
            />
            <ButtonItem
              buttonOptions={{
                text: t("awards.send"),
                type: "default",
                disabled: !employeeId || !employeeCompanyId,
                onClick: () => {
                  const result = validateAll();
                  if (result?.isValid) {
                    addAward();
                    setPopupVisibleAdd(false);
                  }
                },
              }}
            />
          </Form>
        </Popup>
      </div>
    </ScrollView>
  );
};
